import React, { useState } from 'react';
import { FileUpload, IUploadedFile } from '../FileUpload';
import { AlleleFormat, CalcRMPResponse, KitPopulationClient, LabClient, LabKitClient, ProfilesClient, RareAlleleType } from '@app/api2/api';
import { Container, FormDropdown, Form, Message, FormInput } from 'semantic-ui-react';
import { buildUrl, AppRoutes } from '../AppRoutes';
import SemanticField from '@app/components/SemanticField';
import { toAlleleFormatName } from '@common/AlleleFormatNames';
import { FormErrorList } from '../FormErrorList';
import * as yup from "yup";
import { BPForm } from '@common/BPForm';
import { RareAllelesFreqSelect } from '@common/RareAllelesFreqSelect';
import { BPContainer } from '@/components/bp/bpcontainer';
import SemanticFormikSelect from '../Common/Forms/SemanticFormikSelect';
import { useQuery } from '@tanstack/react-query';


interface Values {
    populationId: number;
    fst: number;
    // sampleId: number;
    rareAlleleType: RareAlleleType;
}

interface Preview {
    rmp: CalcRMPResponse;
}

type SampleCalcRMPProps = {
    sampleId: number;
    labId: number;
    caseId?: number;
};

export const SampleCalcRMP = (props: SampleCalcRMPProps) => {
    const { sampleId, labId, caseId } = props;
    const [preview, setPreview] = useState<Preview>({ rmp: { rmpModel: { fst: 0.01, id: 0, oneDivRMP: 0, populationId: 0, rareAlleleType: RareAlleleType.MinFreq, rmp: 0 } } });
    const kitClient = new LabKitClient();
    const sampleClient = new ProfilesClient();
    const populationClient = new KitPopulationClient();


    const populationsQuery = useQuery({
        queryKey: ['populationsForLab', labId, caseId],
        queryFn: async () => {
            return await (caseId && caseId != 0 ? populationClient.getAllForCase(labId, caseId) : populationClient.getAllForLab(labId));
        }
    });


    return (
        <BPContainer>
            <BPForm
                initialValues={{ rareAlleleType: RareAlleleType.MinFreq, fst: 0.01 } as Values}
                enableReinitialize={false}
                vaildationSchema={yup.object().shape({
                    populationId: yup.number().required().label(window.translation.common.population),
                    rareAlleleType: yup.mixed().required().label(window.translation.parameter.names.rareAlleleFreqType),
                    fst: yup.number().required().label(window.translation.parameter.names.fstCorrection),
                })}
                onSubmit={async (values) => {
                    await kitClient.getAll(labId)
                    const response = await sampleClient.calculateSampleRMP(sampleId, {
                        rareAlleleType: values.rareAlleleType,
                        sampleId: sampleId,
                        fst: values.fst, populationId: values.populationId
                    })
                    setPreview({ rmp: response })
                    window.location.assign(caseId ? buildUrl(AppRoutes.SampleDetails, { caseId: caseId, labId: labId, sampleId: sampleId }) :buildUrl(AppRoutes.SampleDetailsWithoutCase, { labId: labId, sampleId: sampleId }))
                }}
                canSubmit={(values, formik) => {
                    return formik.isValid;
                }}>
                {() => (
                    <>
                        <SemanticFormikSelect
                            label={window.translation.common.population}
                            name={'populationId'}
                            options={populationsQuery.data?.map((p) => ({
                                key: p.id,
                                value: p.id,
                                text: p.name, 
                                // content: <>{p.name} ({p.ownerCaseName})</>
                            })) || []}
                            selection
                            required />

                        <SemanticField
                            label={window.translation.parameter.names.rareAlleleFreqType}
                            name={'rareAlleleType'}
                            component={Form.Dropdown}
                            options={[]}
                            selection
                            required
                            optionsSource={async () => {
                                const rareAlleleTypes = [{ type: RareAlleleType.MinFreq, label: "BP" }, { type: RareAlleleType.PopN, label: "EFM" }, { type: RareAlleleType.PopLocN, label: "STRmix" }]
                                return [...rareAlleleTypes.map((type) => { return { key: type.type, value: type.type, text: type.label }; })];
                            }} />

                        <SemanticField label={window.translation.parameter.names.fstCorrection}
                            name={'fst'}
                            search
                            selection
                            component={Form.Dropdown}
                            options={[0.01, 0.02, 0.03].map(x => ({ id: x, value: x, text: x }))}
                        />
                    </>
                )}
            </BPForm>


        </BPContainer>

    );
};